import React, { useEffect, useState, useRef } from "react";
import moment from "moment-timezone";
import { useTranslation } from 'react-i18next';
import TimeSelection from "./TimeSelection";
import ReservationDetails from "../Modal/ReservationDetails";
import Modal from '../Modal/Modal';
import reservationsWS from '../../services/wsReservations';
import { toPersianDigits } from '../../utils/persianNumbers';

const Timeline = ({
  selectedDate,
  reservationData,
  rooms: roomsProp = [],
  onTimeSelect,
  isModalOpen,
  onReservationDelete,
}) => {
  const { t, i18n } = useTranslation();
  const isPersian = i18n.language === 'fa';
  const [rooms, setRooms] = useState(roomsProp);
  const [currentDate, setCurrentDate] = useState(moment().format("YYYY-MM-DD"));
  const [selectedReservation, setSelectedReservation] = useState(null);
  const timelineRef = useRef(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [reservationToEdit, setReservationToEdit] = useState(null);
  const [holds, setHolds] = useState([]); // ephemeral holds from others
  const holdsRef = useRef({});

  // Update rooms when prop changes (e.g., organization switch)
  useEffect(() => {
    setRooms(roomsProp);
  }, [roomsProp]);

  // Listen for ephemeral hold events and reflect them on the grid
  useEffect(() => {
    const unsub = reservationsWS.addHoldListener((payload) => {
      const { action, date, resource_id, start_minute, end_minute, user_name } = payload || {};
      const todayStr = (selectedDate || currentDate);
      if (!date || date !== todayStr) return;
      if (!resource_id) return;

      const key = `${payload.hold_id || ''}-${resource_id}`;
      const next = { ...holdsRef.current };
      if (action === 'hold_end') {
        delete next[key];
      } else {
        next[key] = {
          resource_id: Number(resource_id),
          start_minute: Number(start_minute) || 0,
          end_minute: Number(end_minute) || Number(start_minute) || 0,
          user_name: user_name || 'Someone',
          ts: payload.ts || new Date().toISOString(),
        };
      }
      holdsRef.current = next;
      setHolds(Object.values(next));
    });
    const unsubSnap = reservationsWS.addHoldSnapshotListener((list, meta) => {
      const todayStr = (selectedDate || currentDate);
      if (!meta || meta.date !== todayStr) return;
      const next = {};
      list.forEach((p, idx) => {
        const key = `${p.hold_id || idx}-${p.resource_id}`;
        next[key] = {
          resource_id: Number(p.resource_id),
          start_minute: Number(p.start_minute) || 0,
          end_minute: Number(p.end_minute) || Number(p.start_minute) || 0,
          user_name: p.user_name || 'Someone',
          ts: p.ts || new Date().toISOString(),
        };
      });
      holdsRef.current = next;
      setHolds(Object.values(next));
    });
    // Prune stale holds (15s TTL)
    const pruneInterval = setInterval(() => {
      const now = Date.now();
      const next = { ...holdsRef.current };
      Object.entries(next).forEach(([k, v]) => {
        const t = Date.parse(v.ts || 0);
        if (!t || now - t > 15000) {
          delete next[k];
        }
      });
      if (Object.keys(next).length !== Object.keys(holdsRef.current).length) {
        holdsRef.current = next;
        setHolds(Object.values(next));
      }
    }, 5000);
    return () => { if (unsub) unsub(); if (unsubSnap) unsubSnap(); clearInterval(pruneInterval); };
  }, [selectedDate, currentDate]);

  // Removed unused reservations state and fetch effect

  useEffect(() => {
    if (selectedDate) {
      setCurrentDate(selectedDate);
    }
  }, [selectedDate]);

  // When date changes, clear out local holds immediately to avoid cross-day bleed
  useEffect(() => {
    holdsRef.current = {};
    setHolds([]);
  }, [currentDate]);

  // Modify useEffect for scroll behavior
  useEffect(() => {
    if (timelineRef.current) {
      const now = moment();
      const currentHalfHour = now.hours() * 2 + (now.minutes() >= 30 ? 1 : 0);
      const scrollPosition = currentHalfHour * 70; // 70px is the width of each time cell
      timelineRef.current.scrollLeft = scrollPosition; // Remove the centering calculation
    }
  }, []);

  // Removed unused getItemRoom helper

  const handleTimeSelection = (selection) => {
    if (onTimeSelect) {
      const adjustedSelection = {
        ...selection,
        endTime: selection.endTime === "24:00" ? "23:59" : selection.endTime,
        date: selectedDate,
      };
      onTimeSelect(adjustedSelection);
    }
  };

  return (
    <div
      ref={timelineRef}
      style={{
        overflowX: "auto",
        width: "100%",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `150px repeat(48, 70px)`,
          gridAutoRows: "60px",
          border: "1px solid var(--border)",
          borderRadius: "12px",
          position: "relative",
          minWidth: "calc(150px + 3360px)",
          boxShadow: "var(--shadow)",
          background: "var(--surface)",
        }}
      >
        <div style={{ gridColumn: "1 / -1", display: "contents" }}>
          <div
            style={{
              backgroundColor: "var(--surface-elevated)",
              fontWeight: "bold",
              textAlign: "center",
              lineHeight: "60px",
              position: "sticky",
              left: 0,
              zIndex: 2,
              borderRight: "1px solid var(--border)",
            }}
          >
            {t('timeline_rooms')}
          </div>
          {[...Array(48)].map((_, halfHour) => {
            const now = moment();
            const currentHalfHour =
              now.hours() * 2 + (now.minutes() >= 30 ? 1 : 0);

            // removed unused cellTime

            return (
              <div
                key={halfHour}
                style={{
                  backgroundColor:
                    halfHour === currentHalfHour
                      ? "color-mix(in oklab, var(--color-primary) 10%, var(--surface))"
                      : "var(--surface-elevated)",
                  textAlign: "left",
                  lineHeight: "60px",
                  borderLeft: "none",
                  position: "relative",
                  fontSize: "12px",
                }}
              >
                <div>
                  {(() => {
                    const timeStr = halfHour === 48
                      ? "23:59"
                      : `${Math.floor(halfHour / 2)
                          .toString()
                          .padStart(2, "0")}:${halfHour % 2 === 0 ? "00" : "30"}`;
                    return isPersian ? toPersianDigits(timeStr) : timeStr;
                  })()}
                </div>
                {/* Vertical tick marks */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "12px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        width: "1px",
                        height: i === 0 || i === 5 ? "12px" : "8px",
                        backgroundColor: "var(--border)",
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        {rooms.map((room, index) => (
          <React.Fragment key={room.id}>
            <div
              style={{
                 backgroundColor: index % 2 === 0 ? "var(--surface)" : "var(--surface-alt)",
                fontWeight: "bold",
                textAlign: "center",
                 borderTop: "1px solid var(--border)",
                position: "sticky",
                left: 0,
                zIndex: 2,
                 borderRight: "1px solid var(--border)",
                whiteSpace: "normal", // Allows text to wrap
                wordBreak: "break-word", // Breaks long words if necessary
                padding: "10px", // Adds some spacing
                height: "auto", // Allows the div to expand
                display: "flex",
                flexDirection: "column", // Stack items vertically
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span>{room.name}</span>
               <span style={{ fontSize: "14px", color: "var(--text-muted)" }}>
                {t('timeline_capacity')} {isPersian ? toPersianDigits(room.capacity) : room.capacity}
              </span>
            </div>
            <TimeSelection
              roomId={room.id}
              onSelectionChange={room.can_create_reservation ? handleTimeSelection : null}
              isModalOpen={isModalOpen}
              dateForHold={selectedDate || currentDate}
              canCreateReservation={room.can_create_reservation}
            />
            {/* Render in-progress holds (ephemeral) */}
            {holds
              .filter(h => h.resource_id === room.id)
              .map((h, i) => {
                const pixelsPerMinute = 70 / 30;
                const startPosition = (h.start_minute || 0) * pixelsPerMinute;
                const width = Math.max(2, ((h.end_minute || h.start_minute || 0) - (h.start_minute || 0)) * pixelsPerMinute);
                return (
                  <div
                    key={`hold-${room.id}-${i}`}
                    style={{
                      position: 'absolute',
                      left: `${150 + startPosition}px`,
                      top: `${(index + 1) * 60 + 6}px`,
                      width: `${width}px`,
                      height: '48px',
                        background: 'repeating-linear-gradient(45deg, color-mix(in oklab, var(--color-warning) 25%, transparent), color-mix(in oklab, var(--color-warning) 25%, transparent) 10px, color-mix(in oklab, var(--color-warning) 40%, transparent) 10px, color-mix(in oklab, var(--color-warning) 40%, transparent) 20px)',
                       border: '1px dashed color-mix(in oklab, var(--color-warning) 80%, transparent)',
                      zIndex: 2,
                      pointerEvents: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                       color: 'color-mix(in oklab, var(--color-warning) 70%, black)',
                      fontSize: '12px',
                    }}
                    title={`${t('timeline_reserving_by')} ${h.user_name}`}
                  >
                    {t('timeline_reserving')}
                  </div>
                );
              })}
            {reservationData &&
              reservationData.map((reservation, i) => {
                if (reservation.roomId === room.id) {
                  const startTime = moment(
                    reservation.occurrence_start || `${reservation.date} ${reservation.startTime}`,
                    reservation.occurrence_start ? undefined : "YYYY-MM-DD HH:mm"
                  );
                  const endTime = moment(
                    reservation.occurrence_end || `${reservation.date} ${reservation.endTime}`,
                    reservation.occurrence_end ? undefined : "YYYY-MM-DD HH:mm"
                  );

                  // Calculate precise positions using minutes
                  const startMinutes =
                    startTime.hours() * 60 + startTime.minutes();
                  const endMinutes = endTime.hours() * 60 + endTime.minutes();

                  // Convert minutes to pixel position (70px per 30 minutes = 2.333px per minute)
                  const pixelsPerMinute = 70 / 30;
                  const startPosition = startMinutes * pixelsPerMinute;
                  const width = (endMinutes - startMinutes) * pixelsPerMinute;

                  return (
                    <div
                      key={i}
                      onClick={() => setSelectedReservation(reservation)}
                      style={{
                        position: "absolute",
                        left: `${150 + startPosition}px`,
                        top: `${(index + 1) * 60}px`,
                        width: `${width}px`,
                        height: "60px",
                        background: "linear-gradient(to right, var(--color-accent-50), color-mix(in oklab, var(--color-accent) 22%, transparent) 2%, color-mix(in oklab, var(--color-accent) 22%, transparent) 98%, var(--color-accent-50))",
                        zIndex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        fontSize: "14px",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        border: "1px solid var(--border)",
                        borderRadius: "10px",
                      }}
                    >
                      {reservation.title || t('timeline_reserved')}
                    </div>
                  );
                }
                return null;
              })}
          </React.Fragment>
        ))}
      </div>

      {selectedReservation && (
        <ReservationDetails
          reservation={selectedReservation}
          onClose={() => setSelectedReservation(null)}
          onDelete={() => {
            onReservationDelete();
            setSelectedReservation(null);
          }}
          onEdit={(reservation) => {
            setReservationToEdit(reservation);
            setEditModalOpen(true);
            setSelectedReservation(null);
          }}
        />
      )}
      {editModalOpen && reservationToEdit && (
        <Modal
          onClose={() => {
            setEditModalOpen(false);
            setReservationToEdit(null);
          }}
          onEventEdited={onReservationDelete}
          rooms={rooms}
          reservation={reservationToEdit}
        />
      )}
    </div>
  );
};

export default Timeline;
